import * as React from 'react';

import {
  AccountType,
  enumAccountType,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Checkbox,
  ConsoleDialog,
  MultiSelect,
  PhoneField,
  TextField,
  TextArea,
  maybe,
  ContentSection,
  Label,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { LucideCheck } from 'lucide-react';

import { AccountFormState } from '../../_state/_types';
import * as accountEditingState from '../../_state/accountEditing.state';
import { useTranslations } from '../../i18n/useTranslations';

import { AddressFormFields } from './AddressFormFields';

import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import {
  SelectableUser,
  useUsersQuery,
} from '~/shared/components/select-fields/SelectUser';
import { keymap } from '~/shared/keymap';

export type AccountCreateDialogProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onAccountCreated?: (accountId: string) => void;
  initialValues?: Partial<AccountFormState>;
};

export const AccountCreateDialog = ({
  isOpen,
  onOpenChange,
  onAccountCreated,
  initialValues,
}: AccountCreateDialogProps) => {
  const toast = useToast();
  const t = useTranslations();

  const createAccount = useSetAtom(accountEditingState.$createAccount);
  const resetAccountForm = useSetAtom(accountEditingState.$resetAccountForm);
  const resetAddressForm = useSetAtom(accountEditingState.$resetAddressForm);
  const canSubmitForm = useAtomValue(accountEditingState.$canSubmitAccountForm);
  const [accountForm, updateAccountForm] = useAtom(
    accountEditingState.$accountForm,
  );

  const hasUnsavedChanges = useAtomValue(
    accountEditingState.$hasUnsavedAccountChanges,
  );

  const { data: users } = useUsersQuery();

  const formData = accountForm;

  const userOptions = React.useMemo(
    () =>
      (users ?? []).map((user: SelectableUser) => ({
        key: user.id,
        label: [user.firstName, user.lastName].filter(Boolean).join(' '),
      })),
    [users],
  );

  const handleFieldChange = React.useCallback(
    <K extends keyof AccountFormState>(field: K) =>
      (value: AccountFormState[K]) => {
        updateAccountForm({ [field]: value });
      },
    [updateAccountForm],
  );

  const fieldProps = React.useCallback(
    <K extends keyof AccountFormState>(key: K) => ({
      value: formData[key],
      onChange: handleFieldChange(key),
    }),
    [formData, handleFieldChange],
  );

  const handleRelationshipChange = React.useCallback(
    (field: 'isCustomer' | 'isSupplier' | 'isCarrier') =>
      (checked: boolean) => {
        updateAccountForm({
          relationship: {
            isCustomer: formData.relationship?.isCustomer ?? false,
            isSupplier: formData.relationship?.isSupplier ?? false,
            isCarrier: formData.relationship?.isCarrier ?? false,
            [field]: checked,
          },
        });
      },
    [updateAccountForm, formData.relationship],
  );

  const handleSubmit = React.useCallback(async () => {
    if (!canSubmitForm) return;

    try {
      const accountId = await createAccount();
      toast.success(t.accountsView.messages.createSuccess);
      if (accountId) onAccountCreated?.(accountId);
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.accountsView.messages.error,
      );
    }
  }, [canSubmitForm, createAccount, toast, t, onAccountCreated, onOpenChange]);

  const handleCancel = React.useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  React.useEffect(() => {
    resetAccountForm(initialValues);
    resetAddressForm();
  }, [isOpen, resetAccountForm, resetAddressForm, initialValues]);

  return (
    <ConsoleDialog
      className="@AccountCreateDialog"
      title={t.accountsView.dialog.title.create}
      isOpen={isOpen}
      onOpenChange={handleCancel}
      askBeforeClosing={hasUnsavedChanges}
      content={
        <div className="space-y-6">
          {/* Account Information Section */}
          <ContentSection title={t.accountsView.form.sections.accountDetails}>
            <div className="grid grid-cols-2 gap-4">
              <TextField
                label={t.accountsView.form.fields.name}
                placeholder={t.accountsView.form.placeholders.companyName}
                autoFocus
                isRequired
                {...fieldProps('name')}
              />
              <SelectEnum<AccountType>
                enumValue={enumAccountType}
                label={t.accountsView.form.fields.type}
                isRequired
                value={maybe(formData.type)}
                onChange={handleFieldChange('type')}
              />
              <TextField
                label={t.accountsView.form.fields.email}
                type="email"
                placeholder={t.accountsView.form.placeholders.email}
                {...fieldProps('email')}
              />
              <PhoneField
                label={t.accountsView.form.fields.phone}
                placeholder={t.accountsView.form.placeholders.phone}
                {...fieldProps('phone')}
              />
              <TextField
                label={t.accountsView.form.fields.website}
                type="url"
                placeholder={t.accountsView.form.placeholders.website}
                {...fieldProps('website')}
              />
              <MultiSelect
                label={t.accountsView.form.fields.accountManagers}
                options={userOptions}
                selectedKeys={formData.accountManagerIds}
                onSelectionChange={handleFieldChange('accountManagerIds')}
                placeholder={t.accountsView.form.placeholders.accountManagers}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label aria-required>
                {t.accountsView.form.fields.relationship}
              </Label>
              <div className="flex gap-4">
                <Checkbox
                  isSelected={formData.relationship?.isCustomer}
                  onChange={handleRelationshipChange('isCustomer')}
                >
                  {t.accountsView.relationship.customer}
                </Checkbox>
                <Checkbox
                  isSelected={formData.relationship?.isSupplier}
                  onChange={handleRelationshipChange('isSupplier')}
                >
                  {t.accountsView.relationship.supplier}
                </Checkbox>
                <Checkbox
                  isSelected={formData.relationship?.isCarrier}
                  onChange={handleRelationshipChange('isCarrier')}
                >
                  {t.accountsView.relationship.carrier}
                </Checkbox>
              </div>
            </div>
          </ContentSection>

          {/* Address Section */}
          <ContentSection title={t.accountsView.form.sections.primaryAddress}>
            <AddressFormFields autoFocus={false} />
          </ContentSection>

          {/* Notes Section */}
          <ContentSection>
            <TextArea
              label={t.accountsView.form.fields.notes}
              placeholder={t.accountsView.form.placeholders.notes}
              rows={4}
              value={maybe(formData.notes)}
              onChange={handleFieldChange('notes')}
            />
          </ContentSection>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" color="neutral" onClick={handleCancel}>
            {t.accountsView.dialog.actions.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            isDisabled={!canSubmitForm}
            onClick={handleSubmit}
            keyboardShortcut={keymap.save}
          >
            {t.accountsView.dialog.actions.create}
          </Button>
        </div>
      }
    />
  );
};
